import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Tabs from 'cccisd-tabs';
import Tooltip from 'cccisd-tooltip';
import Loader from 'cccisd-loader';

import IndividualDataTab from './IndividualDataTab';
import AggregateDataTab from './AggregateDataTab';

import {
    queryAllAnswerData,
    computeScoreAverages,
    setAggregateScoreAverages,
    queryAdministeredPlansData,
} from '../../reducers/SmartWrapDashboard/dashboardSlice';
import { queryCareCoordinatorYouthList } from '../../reducers/SmartWrapCareCoordinator/careCoordinatorSlice';
import { querySupervisorYouthList } from '../../reducers/SmartWrapSupervisor/supervisorSlice';

const isSupervisor = window.cccisd?.fortress?.user?.acting?.role?.handle === 'supervisor';

const Dashboard = () => {
    const [loading, setLoading] = useState(true);

    // state
    const dispatch = useDispatch();
    const allAnswerData = useSelector(state => state.app.smartwrap.dashboard.allAnswerData);
    const youthList = useSelector(state =>
        isSupervisor ? state.app.smartwrap.supervisor.youthList : state.app.smartwrap.careCoordinator.youthList
    );
    const youthListFetched = useSelector(state =>
        isSupervisor
            ? state.app.smartwrap.supervisor.youthListFetched
            : state.app.smartwrap.careCoordinator.youthListFetched
    );
    const selectedYouth = useSelector(state => state.app.smartwrap.dashboard.selectedYouth);
    const startDate = useSelector(state => state.app.smartwrap.dashboard.filters.startDate);
    const endDate = useSelector(state => state.app.smartwrap.dashboard.filters.endDate);
    const planName = useSelector(state => state.app.smartwrap.dashboard.filters.planName);
    const administeredPlansData = useSelector(state => state.app.smartwrap.dashboard.administeredPlansData);
    const selectedCareCoordinator = useSelector(state => state.app.smartwrap.dashboard.selectedCareCoordinator);
    const filteredYouthList = useSelector(state =>
        isSupervisor ? state.app.smartwrap.supervisor.filteredYouthList : state.app.smartwrap.careCoordinator.youthList
    );

    // dispatch queries to be loaded into state if not already
    useEffect(() => {
        const dispatchQueries = async () => {
            setLoading(true);

            if (allAnswerData === null) {
                await dispatch(queryAllAnswerData());
            }
            if (!youthListFetched) {
                await dispatch(isSupervisor ? querySupervisorYouthList() : queryCareCoordinatorYouthList());
            }
            if (!administeredPlansData || Object.keys(administeredPlansData).length === 0) {
                await dispatch(queryAdministeredPlansData());
            }

            setLoading(false);
        };

        dispatchQueries();
    }, [youthList, allAnswerData]);

    // Recompute scoreAverages whenever data, filters, selected youth, or CC filter change
    useEffect(() => {
        if (!allAnswerData || !youthList.length) return;

        dispatch(
            setAggregateScoreAverages(
                computeScoreAverages(
                    allAnswerData,
                    filteredYouthList,
                    { startDate, endDate, planName },
                    selectedCareCoordinator,
                    selectedYouth
                )
            )
        );
    }, [allAnswerData, filteredYouthList, startDate, endDate, planName, selectedYouth, selectedCareCoordinator]);

    const tabList = [
        {
            name: 'individual',
            title: (
                <Tooltip title="Shows scores to each survey item for each individual caregiver and/or youth, grouped by domain.">
                    Individual Data
                </Tooltip>
            ),
            content: <IndividualDataTab />,
        },
        {
            name: 'aggregate',
            title: 'Aggregate Data',
            content: <AggregateDataTab />,
        },
    ];

    if (loading) {
        return <Loader loading />;
    }

    return (
        <div style={{ marginTop: '2rem' }}>
            <Tabs tabList={tabList} />
        </div>
    );
};

export default Dashboard;
